/* ==========================================================================
   ORRA Jewellery — progressive enhancement
   Vanilla JS, no dependencies. Every feature degrades to native Shopify
   behaviour (links, forms, page loads) when this file fails to run.
   ========================================================================== */
(() => {
  'use strict';

  const A = window.AURA || { routes: {}, strings: {} };
  const S = A.strings || {};
  const R = A.routes || {};
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Utilities ---------- */
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };

  const announce = (message) => {
    const region = $('[data-live-region]');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, 60);
  };

  let toastTimer;
  const toast = (message) => {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 3200);
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const fetchJSON = async (url, options = {}) => {
    const res = await fetch(url, {
      ...options,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(options.headers || {}) }
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data.description || data.message || S.cartError || 'Error');
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return data;
  };

  /* ---------- Scroll lock & focus trap ---------- */
  let lockCount = 0;
  const lockScroll = () => { lockCount += 1; document.body.classList.add('is-locked'); };
  const unlockScroll = () => { lockCount = Math.max(0, lockCount - 1); if (!lockCount) document.body.classList.remove('is-locked'); };

  const trapFocus = (container) => {
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      const items = $$(FOCUSABLE, container).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (!items.length) { e.preventDefault(); container.focus(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === container)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    container.addEventListener('keydown', handler);
    return () => container.removeEventListener('keydown', handler);
  };

  /* ---------- Overlay layer manager (drawers, search, modals) ---------- */
  const overlay = $('[data-overlay]');
  const Layer = {
    stack: [],
    open(panel, trigger, { focusTarget } = {}) {
      if (!panel || panel.classList.contains('is-open')) return;
      const entry = { panel, trigger: trigger || document.activeElement, release: trapFocus(panel) };
      this.stack.push(entry);
      panel.classList.add('is-open');
      panel.removeAttribute('aria-hidden');
      $$(`[aria-controls="${panel.id}"]`).forEach((t) => t.setAttribute('aria-expanded', 'true'));
      overlay && overlay.classList.add('is-visible');
      lockScroll();
      const target = focusTarget || $('[data-autofocus]', panel) || $(FOCUSABLE, panel) || panel;
      window.setTimeout(() => target.focus({ preventScroll: true }), reduceMotion ? 0 : 60);
      panel.dispatchEvent(new CustomEvent('aura:open', { bubbles: true }));
    },
    close(panel) {
      const idx = this.stack.findIndex((e) => e.panel === panel);
      if (idx === -1) return;
      const [entry] = this.stack.splice(idx, 1);
      entry.release();
      panel.classList.remove('is-open');
      $$(`[aria-controls="${panel.id}"]`).forEach((t) => t.setAttribute('aria-expanded', 'false'));
      if (!this.stack.length) overlay && overlay.classList.remove('is-visible');
      unlockScroll();
      if (entry.trigger && document.contains(entry.trigger)) entry.trigger.focus({ preventScroll: true });
      panel.dispatchEvent(new CustomEvent('aura:close', { bubbles: true }));
    },
    top() { return this.stack[this.stack.length - 1]; },
    closeTop() { const t = this.top(); if (t) this.close(t.panel); }
  };

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && Layer.stack.length) { e.preventDefault(); Layer.closeTop(); }
  });
  overlay && overlay.addEventListener('click', () => { while (Layer.stack.length) Layer.closeTop(); });

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      Layer.open(document.getElementById(opener.dataset.drawerOpen), opener);
      return;
    }
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      const panel = closer.closest('[data-drawer], .modal');
      if (panel) { e.preventDefault(); Layer.close(panel); }
    }
    const modalOpener = e.target.closest('[data-modal-open]');
    if (modalOpener) {
      const modal = document.getElementById(modalOpener.dataset.modalOpen);
      if (modal) { e.preventDefault(); Layer.open(modal, modalOpener); }
    }
  });

  /* ---------- Header: scroll state, height var, mega menu ---------- */
  const initHeader = () => {
    const wrapper = $('[data-header-wrapper]');
    const header = $('[data-header]');
    if (!header) return;

    const setHeight = () => document.documentElement.style.setProperty('--aura-header-actual', `${wrapper.offsetHeight}px`);
    setHeight();
    window.addEventListener('resize', debounce(setHeight, 150));

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        header.classList.toggle('is-scrolled', window.scrollY > 24);
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    const items = $$('[data-mega-item]', header);
    const closeAll = (except) => items.forEach((item) => {
      if (item === except) return;
      item.classList.remove('is-open');
      const btn = $('[data-mega-trigger]', item);
      btn && btn.setAttribute('aria-expanded', 'false');
      header.classList.remove('is-open');
    });
    const openItem = (item) => {
      closeAll(item);
      item.classList.add('is-open');
      header.classList.add('is-open');
      $('[data-mega-trigger]', item).setAttribute('aria-expanded', 'true');
    };
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)');

    items.forEach((item) => {
      const btn = $('[data-mega-trigger]', item);
      let hoverTimer;
      btn.addEventListener('click', () => {
        clearTimeout(hoverTimer);
        if (item.classList.contains('is-open')) closeAll(); else openItem(item);
      });
      item.addEventListener('mouseenter', () => { if (!fine.matches) return; clearTimeout(hoverTimer); hoverTimer = setTimeout(() => openItem(item), 120); });
      item.addEventListener('mouseleave', () => { if (!fine.matches) return; clearTimeout(hoverTimer); hoverTimer = setTimeout(() => closeAll(), 180); });
      item.addEventListener('focusout', (e) => { if (!item.contains(e.relatedTarget)) { item.classList.remove('is-open'); btn.setAttribute('aria-expanded', 'false'); header.classList.remove('is-open'); } });
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && item.classList.contains('is-open')) { e.stopPropagation(); clearTimeout(hoverTimer); closeAll(); btn.focus(); }
      });
    });
    document.addEventListener('click', (e) => { if (!e.target.closest('[data-mega-item]')) closeAll(); });
  };

  /* ---------- Disclosures (mobile menu, generic) ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-disclosure]');
    if (!btn) return;
    const target = document.getElementById(btn.getAttribute('aria-controls'));
    if (!target) return;
    const open = btn.getAttribute('aria-expanded') !== 'true';
    btn.setAttribute('aria-expanded', String(open));
    target.hidden = !open;
  });

  /* ---------- Predictive search ---------- */
  const initSearch = () => {
    const modal = $('[data-search-modal]');
    if (!modal) return;
    const input = $('[data-predictive-input]', modal);
    const results = $('[data-predictive-results]', modal);
    const status = $('[data-predictive-status]', modal);
    const defaults = $('[data-predictive-default]', modal);
    let controller;
    let activeIndex = -1;

    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-search-open]');
      if (opener) { e.preventDefault(); Layer.open(modal, opener, { focusTarget: input }); }
      if (e.target.closest('[data-search-close]')) { e.preventDefault(); Layer.close(modal); }
    });

    if (!A.predictiveSearch || !R.predictiveSearch) return;

    const options = () => $$('[data-predictive-option]', results);
    const setActive = (index) => {
      const opts = options();
      opts.forEach((o) => o.setAttribute('aria-selected', 'false'));
      activeIndex = index;
      if (index >= 0 && opts[index]) {
        opts[index].setAttribute('aria-selected', 'true');
        input.setAttribute('aria-activedescendant', opts[index].id);
        opts[index].scrollIntoView({ block: 'nearest' });
      } else {
        input.removeAttribute('aria-activedescendant');
      }
    };

    const clear = () => {
      results.innerHTML = '';
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      defaults && (defaults.hidden = false);
      activeIndex = -1;
    };

    const search = debounce(async (term) => {
      if (controller) controller.abort();
      if (!term) { clear(); return; }
      controller = new AbortController();
      results.classList.add('is-loading');
      try {
        const url = `${R.predictiveSearch}?q=${encodeURIComponent(term)}&resources[type]=product,collection,page,article,query&resources[limit]=4&resources[limit_scope]=each&section_id=predictive-search`;
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) throw new Error(res.status);
        const html = await res.text();
        const doc = parseHTML(html);
        const body = $('.shopify-section', doc) || doc.body;
        results.innerHTML = body.innerHTML;
        input.setAttribute('aria-expanded', 'true');
        defaults && (defaults.hidden = true);
        activeIndex = -1;
        const countEl = $('[data-predictive-count]', results);
        const count = countEl ? Number(countEl.dataset.predictiveCount) : 0;
        if (status) status.textContent = count ? (S.searchResults || '').replace('[count]', count) : (S.searchNoResults || '');
      } catch (err) {
        if (err.name !== 'AbortError') clear();
      } finally {
        results.classList.remove('is-loading');
      }
    }, 260);

    input.addEventListener('input', () => search(input.value.trim()));
    input.addEventListener('keydown', (e) => {
      const opts = options();
      if (!opts.length) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive((activeIndex + 1) % opts.length); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(activeIndex <= 0 ? opts.length - 1 : activeIndex - 1); }
      else if (e.key === 'Enter' && activeIndex >= 0) {
        e.preventDefault();
        const link = opts[activeIndex].matches('a') ? opts[activeIndex] : $('a', opts[activeIndex]);
        if (link) window.location.href = link.href;
      }
    });
    modal.addEventListener('aura:close', () => { setActive(-1); });
  };

  /* ---------- Cart ---------- */
  const Cart = {
    drawer: null,
    busy: false,
    sections() {
      const list = ['cart-drawer'];
      const page = $('[data-cart-page]');
      if (page && page.dataset.sectionId) list.push(page.dataset.sectionId);
      return list;
    },
    updateCount(count) {
      $$('[data-cart-count]').forEach((el) => { el.textContent = count > 0 ? count : ''; el.dataset.count = count; });
      $$('[data-cart-link]').forEach((el) => {
        const label = el.getAttribute('aria-label') || '';
        el.setAttribute('aria-label', label.replace(/\d+/, count));
      });
    },
    render(sections) {
      if (!sections) return;
      const drawerHTML = sections['cart-drawer'];
      if (drawerHTML && this.drawer) {
        const fresh = $('[data-cart-drawer-inner]', parseHTML(drawerHTML));
        const current = $('[data-cart-drawer-inner]', this.drawer);
        if (fresh && current) {
          const hadFocusId = document.activeElement && this.drawer.contains(document.activeElement) ? document.activeElement.id : null;
          current.replaceWith(fresh);
          this.updateCount(Number(fresh.dataset.count || 0));
          if (hadFocusId && document.getElementById(hadFocusId)) document.getElementById(hadFocusId).focus();
          else if (this.drawer.classList.contains('is-open')) this.drawer.focus();
          this.loadRecommendations();
        }
      }
      const page = $('[data-cart-page]');
      if (page && sections[page.dataset.sectionId]) {
        const fresh = $('[data-cart-page]', parseHTML(sections[page.dataset.sectionId]));
        if (fresh) page.replaceWith(fresh);
      }
    },
    open(trigger) {
      if (A.cartType === 'page' || !this.drawer) { window.location.href = R.cart; return; }
      Layer.open(this.drawer, trigger);
    },
    async add(form, submitter) {
      if (this.busy) return;
      const btn = submitter || $('[type="submit"]', form);
      const errorEl = $('[data-form-error]', form) || (form.id && $(`[data-form-error-for="${form.id}"]`));
      this.busy = true;
      btn && btn.classList.add('is-loading');
      btn && btn.setAttribute('aria-busy', 'true');
      if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
      try {
        const formData = new FormData(form);
        formData.append('sections', this.sections().join(','));
        formData.append('sections_url', window.location.pathname);
        const res = await fetch(`${R.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: formData });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || S.cartError);
        if (A.cartType === 'page') { window.location.href = R.cart; return; }
        this.render(data.sections);
        announce(S.addedToBag);
        this.open(btn);
      } catch (err) {
        if (errorEl) { errorEl.textContent = err.message || S.cartError; errorEl.hidden = false; }
        else toast(err.message || S.cartError);
      } finally {
        this.busy = false;
        btn && btn.classList.remove('is-loading');
        btn && btn.removeAttribute('aria-busy');
      }
    },
    async change(line, quantity, itemEl) {
      itemEl && itemEl.classList.add('is-loading');
      const errEl = itemEl && $('[data-cart-item-error]', itemEl);
      try {
        const data = await fetchJSON(`${R.cartChange}.js`, {
          method: 'POST',
          body: JSON.stringify({ line, quantity, sections: this.sections(), sections_url: window.location.pathname })
        });
        this.render(data.sections);
        this.updateCount(data.item_count);
        announce(`${data.item_count}`);
      } catch (err) {
        itemEl && itemEl.classList.remove('is-loading');
        if (errEl) { errEl.textContent = err.message || S.cartError; errEl.hidden = false; }
        else toast(err.message || S.cartError);
      }
    },
    async loadRecommendations() {
      const box = this.drawer && $('[data-cart-reco]', this.drawer);
      if (!box || !box.dataset.productId || !R.productRecommendations) return;
      try {
        const res = await fetch(`${R.productRecommendations}?product_id=${box.dataset.productId}&limit=2&intent=complementary&section_id=cart-recommendations`);
        let html = res.ok ? await res.text() : '';
        let doc = parseHTML(html);
        if (!$('[data-reco-loaded]', doc)) {
          const res2 = await fetch(`${R.productRecommendations}?product_id=${box.dataset.productId}&limit=2&intent=related&section_id=cart-recommendations`);
          html = res2.ok ? await res2.text() : '';
          doc = parseHTML(html);
        }
        const loaded = $('[data-reco-loaded]', doc);
        if (loaded) { box.innerHTML = ''; box.appendChild(loaded); box.hidden = false; initWishlistButtons(box); }
      } catch (e) { /* recommendations are optional */ }
    },
    init() {
      this.drawer = $('[data-cart-drawer]');
      document.addEventListener('click', (e) => {
        const link = e.target.closest('[data-cart-open]');
        if (link && A.cartType !== 'page' && this.drawer) { e.preventDefault(); this.open(link); }
      });
      document.addEventListener('submit', (e) => {
        const form = e.target.closest('form[data-product-form]');
        if (!form) return;
        if (form.dataset.validate !== undefined) {
          if (!validateForm(form)) { e.preventDefault(); return; }
        } else if (!form.reportValidity()) { e.preventDefault(); return; }
        e.preventDefault();
        this.add(form, e.submitter);
      });
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-qty-change]');
        if (btn) {
          const item = btn.closest('[data-cart-item]');
          const input = $('[data-qty-input]', item);
          const next = Math.max(0, Number(input.value) + Number(btn.dataset.qtyChange));
          input.value = next;
          this.change(Number(item.dataset.line), next, item);
          return;
        }
        const remove = e.target.closest('[data-cart-remove]');
        if (remove) {
          const item = remove.closest('[data-cart-item]');
          if (item) { e.preventDefault(); this.change(Number(item.dataset.line), 0, item); }
        }
      });
      document.addEventListener('change', debounce((e) => {
        const input = e.target.closest && e.target.closest('[data-qty-input]');
        if (input) {
          const item = input.closest('[data-cart-item]');
          this.change(Number(item.dataset.line), Math.max(0, Number(input.value) || 0), item);
        }
        const note = e.target.closest && e.target.closest('[data-cart-note]');
        if (note) fetchJSON(`${R.cartUpdate}.js`, { method: 'POST', body: JSON.stringify({ note: note.value }) }).catch(() => {});
      }, 300));
      if (this.drawer) this.drawer.addEventListener('aura:open', () => this.loadRecommendations(), { once: true });
    }
  };

  /* ---------- Wishlist (device-local) ---------- */
  const WL_KEY = 'aura:wishlist';
  const Wishlist = {
    read() {
      try { return JSON.parse(window.localStorage.getItem(WL_KEY)) || []; } catch (e) { return []; }
    },
    write(list) {
      try { window.localStorage.setItem(WL_KEY, JSON.stringify(list)); } catch (e) { /* storage unavailable */ }
      document.dispatchEvent(new CustomEvent('aura:wishlist', { detail: list }));
    },
    has(handle) { return this.read().includes(handle); },
    toggle(handle) {
      const list = this.read();
      const i = list.indexOf(handle);
      if (i > -1) list.splice(i, 1); else list.unshift(handle);
      this.write(list.slice(0, 100));
      return i === -1;
    }
  };
  const syncWishlistUI = (root = document) => {
    const list = Wishlist.read();
    $$('[data-wishlist-toggle]', root).forEach((btn) => {
      const on = list.includes(btn.dataset.wishlistToggle);
      btn.setAttribute('aria-pressed', String(on));
      btn.setAttribute('aria-label', on ? btn.dataset.labelRemove : btn.dataset.labelAdd);
    });
    $$('[data-wishlist-count]').forEach((el) => { el.textContent = list.length || ''; el.dataset.count = list.length; });
  };
  const initWishlistButtons = (root) => { if (A.wishlist) syncWishlistUI(root); };
  const initWishlist = () => {
    if (!A.wishlist) return;
    syncWishlistUI();
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-wishlist-toggle]');
      if (!btn) return;
      e.preventDefault();
      const added = Wishlist.toggle(btn.dataset.wishlistToggle);
      announce(added ? S.wishlistAdded : S.wishlistRemoved);
    });
    document.addEventListener('aura:wishlist', () => syncWishlistUI());
    window.addEventListener('storage', (e) => { if (e.key === WL_KEY) syncWishlistUI(); });

    const page = $('[data-wishlist-page]');
    if (page) {
      const grid = $('[data-wishlist-grid]', page);
      const empty = $('[data-wishlist-empty]', page);
      const renderPage = async () => {
        const list = Wishlist.read();
        empty.hidden = list.length > 0;
        grid.hidden = list.length === 0;
        const existing = new Set($$('[data-wishlist-card]', grid).map((el) => el.dataset.wishlistCard));
        $$('[data-wishlist-card]', grid).forEach((el) => { if (!list.includes(el.dataset.wishlistCard)) el.remove(); });
        const missing = list.filter((h) => !existing.has(h));
        const html = await Promise.all(missing.map((h) => fetch(`${R.root.replace(/\/$/, '')}/products/${encodeURIComponent(h)}?section_id=wishlist-card`).then((r) => (r.ok ? r.text() : '')).catch(() => '')));
        html.forEach((h, i) => {
          const card = $('[data-wishlist-card]', parseHTML(h));
          if (card) grid.appendChild(card);
          else Wishlist.write(Wishlist.read().filter((x) => x !== missing[i])); // product no longer exists
        });
        list.forEach((h) => { const el = $(`[data-wishlist-card="${CSS.escape(h)}"]`, grid); el && grid.appendChild(el); });
        syncWishlistUI(grid);
        page.setAttribute('aria-busy', 'false');
      };
      renderPage();
      document.addEventListener('aura:wishlist', debounce(renderPage, 150));
    }
  };

  /* ---------- Form validation (accessible, human messages) ---------- */
  const messageFor = (field) => {
    const v = field.validity;
    if (v.valueMissing) return field.dataset.msgRequired || S.required;
    if (v.typeMismatch && field.type === 'email') return S.email;
    if (v.tooLong) return S.tooLong;
    if (v.patternMismatch) return field.dataset.msgPattern || field.title || S.required;
    return field.validationMessage;
  };
  const setFieldError = (field, message) => {
    const wrap = field.closest('.field, .fieldset') || field.parentElement;
    let err = wrap && $('[data-field-error]', wrap);
    if (message) {
      if (!err) {
        err = document.createElement('p');
        err.className = 'field__error';
        err.dataset.fieldError = '';
        err.id = `${field.id || field.name.replace(/\W/g, '')}-error`;
        wrap.appendChild(err);
      }
      err.textContent = message;
      field.setAttribute('aria-invalid', 'true');
      const describedby = new Set((field.getAttribute('aria-describedby') || '').split(' ').filter(Boolean));
      describedby.add(err.id);
      field.setAttribute('aria-describedby', Array.from(describedby).join(' '));
    } else if (err) {
      err.remove();
      field.removeAttribute('aria-invalid');
      const describedby = (field.getAttribute('aria-describedby') || '').split(' ').filter((id) => id && id !== err.id);
      if (describedby.length) field.setAttribute('aria-describedby', describedby.join(' ')); else field.removeAttribute('aria-describedby');
    }
  };
  const validateForm = (form) => {
    const fields = $$('input, select, textarea', form).filter((f) => !f.disabled && f.type !== 'hidden' && f.willValidate);
    let firstInvalid = null;
    const seenGroups = new Set();
    fields.forEach((f) => {
      if (f.type === 'radio') {
        if (seenGroups.has(f.name)) return;
        seenGroups.add(f.name);
      }
      const valid = f.checkValidity();
      setFieldError(f, valid ? '' : messageFor(f));
      if (!valid && !firstInvalid) firstInvalid = f;
    });
    const summary = $('[data-form-summary]', form);
    if (firstInvalid) {
      if (summary) { summary.hidden = false; summary.textContent = S.formErrorsSummary; }
      firstInvalid.focus();
      return false;
    }
    if (summary) summary.hidden = true;
    return true;
  };
  const initForms = () => {
    $$('form[data-validate]').forEach((form) => {
      form.setAttribute('novalidate', 'novalidate');
      form.addEventListener('submit', (e) => {
        if (form.matches('[data-product-form]')) return; // handled by Cart
        if (!validateForm(form)) { e.preventDefault(); return; }
        const btn = e.submitter || $('[type="submit"]', form);
        btn && btn.classList.add('is-loading');
      });
      form.addEventListener('input', (e) => {
        const f = e.target;
        if (f.getAttribute('aria-invalid') === 'true' && f.checkValidity()) setFieldError(f, '');
      });
      form.addEventListener('change', (e) => {
        const f = e.target;
        if (f.type === 'radio' || f.type === 'checkbox') {
          const first = $(`[name="${CSS.escape(f.name)}"]`, form);
          if (first && first.getAttribute('aria-invalid') === 'true' && first.checkValidity()) setFieldError(first, '');
        }
      });
    });
    $$('select[data-default]').forEach((sel) => { if (sel.dataset.default) sel.value = sel.dataset.default; });
    // Pre-select options from the URL, e.g. ?enquiry=bespoke
    const params = new URLSearchParams(window.location.search);
    $$('[data-param-match]').forEach((input) => {
      const [key, value] = input.dataset.paramMatch.split('=');
      if (params.get(key) === value) input.checked = true;
    });
    const success = $('[data-autofocus].form-status');
    if (success) { success.focus(); }
    // character counters
    $$('[data-char-count]').forEach((input) => {
      const out = document.getElementById(input.dataset.charCount);
      if (!out) return;
      const update = () => {
        const max = Number(input.getAttribute('maxlength')) || 0;
        out.textContent = (S.charactersLeft || '[count]').replace('[count]', Math.max(0, max - input.value.length));
      };
      input.addEventListener('input', update);
      update();
    });
  };

  /* ---------- Localization auto-submit ---------- */
  document.addEventListener('change', (e) => {
    const sel = e.target.closest('[data-auto-submit]');
    if (sel && sel.form) sel.form.submit();
  });

  /* ---------- Product: variants, gallery, engraving, sticky bar ---------- */
  const initProduct = (root) => {
    const dataEl = $('[data-product-json]', root);
    const form = $('form[data-product-form]', root);
    if (!form) return;
    const jsId = $('[data-js-variant-id]', form);
    if (jsId) {
      jsId.disabled = false;
      const noJs = $('[data-no-js-select]', form);
      if (noJs) noJs.disabled = true;
    }
    const idInput = jsId || $('input[name="id"]', form);
    const variants = dataEl ? JSON.parse(dataEl.textContent) : [];
    const picker = $('[data-variant-picker]', root);
    const priceBox = $('[data-product-price]', root);
    const skuEl = $('[data-product-sku]', root);
    const availEl = $('[data-product-availability]', root);
    const addBtns = $$('[data-add-button]', root);
    const sticky = $('[data-sticky-atc]', root);
    const gallery = $('[data-gallery]', root);
    const updateUrl = root.dataset.updateUrl !== 'false';

    const selectedValues = () => {
      if (!picker) return [];
      return $$('[data-option-index]', picker).sort((a, b) => a.dataset.optionIndex - b.dataset.optionIndex).map((group) => {
        if (group.tagName === 'SELECT') return group.value;
        const checked = $('input:checked', group);
        return checked ? checked.value : null;
      });
    };
    const findVariant = (values) => variants.find((v) => v.options.every((o, i) => o === values[i]));

    const refreshAvailability = (values) => {
      $$('[data-option-index]', picker).forEach((group) => {
        const idx = Number(group.dataset.optionIndex);
        const inputs = group.tagName === 'SELECT' ? $$('option', group) : $$('input', group);
        inputs.forEach((input) => {
          const candidate = values.slice();
          candidate[idx] = input.value;
          const match = variants.filter((v) => v.options.every((o, i) => i === idx || o === candidate[i]) && v.options[idx] === input.value);
          const exists = match.length > 0;
          const available = match.some((v) => v.available);
          const label = group.tagName === 'SELECT' ? input : input.nextElementSibling;
          if (group.tagName === 'SELECT') {
            input.textContent = input.dataset.label + (exists && !available ? ` — ${S.soldOut}` : '') + (!exists ? ` — ${S.unavailable}` : '');
          } else if (label) {
            label.classList.toggle('is-soldout', exists && !available);
            label.classList.toggle('is-unavailable', !exists);
            const hint = $('.visually-hidden', label);
            if (hint) hint.textContent = !exists ? `, ${S.unavailable}` : (!available ? `, ${S.soldOut}` : '');
          }
          input.dataset.exists = String(exists);
        });
      });
      // selected value labels
      $$('[data-option-index]', picker).forEach((group) => {
        const out = $(`[data-selected-value="${group.dataset.optionIndex}"]`, picker);
        if (out) out.textContent = values[Number(group.dataset.optionIndex)] || '';
      });
    };

    const applyVariant = (variant) => {
      const buttonLabel = !variant ? S.unavailable : variant.available ? (A.strings.addToBag) : S.soldOut;
      addBtns.forEach((btn) => {
        btn.disabled = !variant || !variant.available;
        const label = $('[data-add-label]', btn);
        if (label) label.textContent = buttonLabel; else btn.textContent = buttonLabel;
      });
      $$('[data-payment-buttons]', root).forEach((el) => { el.hidden = !variant || !variant.available; });
      if (!variant) { if (availEl) availEl.textContent = S.unavailable; return; }
      idInput.value = variant.id;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
      if (priceBox && variant.price_html) $$('[data-product-price]', root).forEach((p) => { p.innerHTML = variant.price_html; });
      if (skuEl) { skuEl.textContent = variant.sku || ''; skuEl.closest('[data-sku-wrap]') && (skuEl.closest('[data-sku-wrap]').hidden = !variant.sku); }
      if (availEl) availEl.textContent = variant.availability_text || '';
      if (variant.media_id && gallery) showMedia(variant.media_id);
      if (updateUrl && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      root.dispatchEvent(new CustomEvent('aura:variant', { detail: variant, bubbles: true }));
    };

    const onChange = (changedIdx) => {
      let values = selectedValues();
      let variant = findVariant(values);
      if (!variant) {
        // Pick the closest real variant that keeps the value the customer just chose
        const chosen = values[changedIdx];
        const fallback = variants.find((v) => v.available && v.options[changedIdx] === chosen) || variants.find((v) => v.options[changedIdx] === chosen);
        if (fallback) {
          fallback.options.forEach((val, i) => {
            const group = $(`[data-option-index="${i}"]`, picker);
            if (!group) return;
            if (group.tagName === 'SELECT') group.value = val;
            else { const input = $$('input', group).find((inp) => inp.value === val); if (input) input.checked = true; }
          });
          values = fallback.options.slice();
          variant = fallback;
        }
      }
      refreshAvailability(values);
      applyVariant(variant);
    };

    if (picker) {
      picker.addEventListener('change', (e) => {
        const group = e.target.closest('[data-option-index]');
        if (group) onChange(Number(group.dataset.optionIndex));
      });
      refreshAvailability(selectedValues());
    }

    /* Gallery */
    function showMedia(mediaId) {
      const item = gallery && $(`[data-media-id="${mediaId}"]`, gallery);
      if (!item) return;
      const track = $('[data-gallery-track]', gallery);
      if (window.matchMedia('(min-width: 990px)').matches) {
        if (track.firstElementChild !== item) track.prepend(item);
        if (item.getBoundingClientRect().top < 0) item.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      } else {
        track.scrollTo({ left: item.offsetLeft - track.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
      }
      updateDots();
    }
    const updateDots = () => {
      if (!gallery) return;
      const track = $('[data-gallery-track]', gallery);
      const dots = $$('[data-gallery-dot]', gallery);
      const counter = $('[data-gallery-counter]', gallery);
      if (!track) return;
      const items = $$('[data-media-id]', track);
      const idx = Math.round(Math.abs(track.scrollLeft) / (track.clientWidth || 1));
      dots.forEach((d, i) => d.setAttribute('aria-current', String(i === idx)));
      if (counter) counter.textContent = `${Math.min(idx + 1, items.length)} / ${items.length}`;
    };
    if (gallery) {
      const track = $('[data-gallery-track]', gallery);
      track && track.addEventListener('scroll', debounce(updateDots, 60), { passive: true });
      $$('[data-gallery-dot]', gallery).forEach((dot) => dot.addEventListener('click', () => showMedia(dot.dataset.galleryDot)));
      updateDots();

      // Zoom: full-screen viewer with the high-res image
      const zoom = $('[data-zoom-modal]', root);
      if (zoom) {
        const zoomImg = $('[data-zoom-image]', zoom);
        gallery.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-zoom]');
          if (!btn) return;
          e.preventDefault();
          zoomImg.src = btn.dataset.zoom;
          zoomImg.alt = btn.dataset.zoomAlt || '';
          Layer.open(zoom, btn);
        });
      }
    }

    /* Engraving / personalisation */
    $$('[data-personalise-toggle]', root).forEach((toggle) => {
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      const sync = () => {
        const on = toggle.checked;
        toggle.setAttribute('aria-expanded', String(on));
        panel.hidden = !on;
        $$('input, select, textarea', panel).forEach((f) => {
          f.disabled = !on;
          if (f.dataset.requiredWhenOn !== undefined) f.required = on;
          if (!on) setFieldError(f, '');
        });
      };
      toggle.addEventListener('change', sync);
      sync();
    });

    /* Sticky add-to-bag — shown once the main button has scrolled above the viewport.
       A scroll check (not IntersectionObserver) so jumps via anchors/scroll restoration work too. */
    if (sticky) {
      const main = $('[data-add-button]:not([data-sticky-button])', root);
      if (main) {
        let ticking = false;
        let shown = null;
        const update = () => {
          ticking = false;
          const rect = main.getBoundingClientRect();
          const show = rect.bottom < 0 && getComputedStyle(main).visibility !== 'hidden';
          if (show === shown) return;
          shown = show;
          sticky.classList.toggle('is-visible', show);
          sticky.setAttribute('aria-hidden', String(!show));
          $$('a, button', sticky).forEach((el) => { el.tabIndex = show ? 0 : -1; });
        };
        window.addEventListener('scroll', () => { if (!ticking) { ticking = true; window.requestAnimationFrame(update); } }, { passive: true });
        window.addEventListener('resize', debounce(update, 150));
        update();
      }
    }
  };

  /* ---------- Collection filters (Section Rendering, no reload) ---------- */
  const initFacets = () => {
    const container = $('[data-facets]');
    if (!container) return;
    const sectionId = container.dataset.sectionId;
    let controller;

    const render = async (url, push = true) => {
      if (controller) controller.abort();
      controller = new AbortController();
      const results = $('[data-facets-results]');
      results && results.setAttribute('aria-busy', 'true');
      try {
        const fetchUrl = new URL(url, window.location.origin);
        fetchUrl.searchParams.set('section_id', sectionId);
        const res = await fetch(fetchUrl.toString(), { signal: controller.signal });
        const doc = parseHTML(await res.text());
        ['[data-facets-results]', '[data-facets-form-mobile]', '[data-facets-active]', '[data-facets-count]'].forEach((sel) => {
          const fresh = $(sel, doc);
          const current = $(sel);
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        });
        if (push) window.history.pushState({ facets: true }, '', url);
        const count = $('[data-facets-count]');
        if (count) announce(count.textContent.trim());
        initWishlistButtons($('[data-facets-results]'));
        initReveal();
      } catch (e) {
        if (e.name !== 'AbortError') window.location.href = url;
      } finally {
        const r = $('[data-facets-results]');
        r && r.setAttribute('aria-busy', 'false');
      }
    };

    const urlFromForm = (form) => {
      const params = new URLSearchParams(new FormData(form));
      // drop empty price inputs to keep URLs clean
      ['filter.v.price.gte', 'filter.v.price.lte'].forEach((k) => { if (params.get(k) === '') params.delete(k); });
      return `${window.location.pathname}?${params.toString()}`;
    };

    document.addEventListener('change', debounce((e) => {
      const form = e.target.form;
      if (!form || !form.matches('[data-facets-form]')) return;
      render(urlFromForm(form));
    }, 350));
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[data-facets-form]');
      if (!form) return;
      e.preventDefault();
      render(urlFromForm(form));
      const drawer = form.closest('[data-drawer]');
      if (drawer) Layer.close(drawer);
    });
    document.addEventListener('click', (e) => {
      const link = e.target.closest('[data-facet-remove]');
      if (!link) return;
      e.preventDefault();
      render(link.href);
    });
    window.addEventListener('popstate', (e) => { if (e.state && e.state.facets) render(window.location.href, false); });
  };

  /* ---------- Product recommendations (lazy) ---------- */
  const initRecommendations = () => {
    $$('[data-recommendations]').forEach((box) => {
      const load = async () => {
        try {
          const res = await fetch(box.dataset.url);
          if (!res.ok) return;
          const loaded = $('[data-reco-loaded]', parseHTML(await res.text()));
          if (!loaded) { box.remove(); return; }
          box.innerHTML = '';
          box.appendChild(loaded);
          box.hidden = false;
          initWishlistButtons(box);
        } catch (e) { /* optional */ }
      };
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { io.disconnect(); load(); } }, { rootMargin: '600px 0px' });
        io.observe(box.parentElement || box);
      } else load();
    });
  };

  /* ---------- Reveal on scroll ---------- */
  let revealObserver;
  function initReveal() {
    const els = $$('.reveal:not(.is-visible), .reveal-stagger:not(.is-visible)');
    if (!els.length) return;
    if (reduceMotion || document.documentElement.classList.contains('motion-off') || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    revealObserver = revealObserver || new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('is-visible'); revealObserver.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    els.forEach((el) => revealObserver.observe(el));
  }

  /* ---------- Theme editor support ---------- */
  const initEditor = () => {
    if (!window.Shopify || !window.Shopify.designMode) return;
    document.addEventListener('shopify:section:load', (e) => {
      initReveal();
      $$('[data-product-section]', e.target).forEach(initProduct);
      initWishlistButtons(e.target);
    });
    document.addEventListener('shopify:block:select', (e) => {
      const mega = e.target.closest('[data-mega-item]');
      if (mega) mega.classList.add('is-open');
    });
  };

  /* ---------- Boot ---------- */
  const boot = () => {
    initHeader();
    initSearch();
    Cart.init();
    initWishlist();
    initForms();
    $$('[data-product-section]').forEach(initProduct);
    initFacets();
    initRecommendations();
    initReveal();
    initEditor();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();

  window.AURA.Layer = Layer;
  window.AURA.Cart = Cart;
  window.AURA.Wishlist = Wishlist;
})();
